<template>
  <div class="page-designer-v3">
    <div class="designer-header">
      <h2 class="section-title">2. 페이지 디자인</h2>
      <p class="section-description">이벤트 페이지의 구조를 시각적으로 구성하세요</p>
      <div class="hierarchy-guide">
        <div class="hierarchy-item">
          <span class="hierarchy-label">페이지</span>
          <span class="hierarchy-arrow">→</span>
        </div>
        <div class="hierarchy-item">
          <span class="hierarchy-label">스텝</span>
          <span class="hierarchy-arrow">→</span>
        </div>
        <div class="hierarchy-item">
          <span class="hierarchy-label">섹션</span>
          <span class="hierarchy-arrow">→</span>
        </div>
        <div class="hierarchy-item">
          <span class="hierarchy-label">컴포넌트</span>
        </div>
      </div>
    </div>

    <!-- Visual Builder -->
    <div class="visual-builder">
      <a-row :gutter="16" class="builder-container">
        <!-- 왼쪽 패널: 컴포넌트 팔레트 -->
        <a-col :span="6" class="component-palette">
          <a-card title="컴포넌트 팔레트" size="small">
            <div class="palette-section">
              <h4>컴포넌트</h4>
              <div class="palette-grid">
                <div
                  v-for="comp in basicComponents"
                  :key="comp.type"
                  class="palette-item"
                  @click="() => handleAddComponent(comp)"
                >
                  <component :is="comp.icon" class="palette-icon" />
                  <span>{{ comp.label }}</span>
                  <a-tooltip :title="comp.description">
                    <info-circle-outlined class="info-icon" />
                  </a-tooltip>
                </div>
              </div>
            </div>

            <div class="palette-help">
              <info-circle-outlined />
              <span>컴포넌트를 클릭하여 선택된 섹션에 추가하세요</span>
            </div>
          </a-card>

          <!-- 빠른 액션 -->
          <a-card title="빠른 액션" size="small" style="margin-top: 16px;">
            <a-space direction="vertical" style="width: 100%;">
              <a-button block @click="addNewStep">
                <plus-outlined /> 새 스텝 추가
              </a-button>
              <a-button block @click="clearAll">
                <clear-outlined /> 모두 지우기
              </a-button>
              <a-button block type="primary" @click="previewPage" class="yellow-button">
                <eye-outlined /> 미리보기
              </a-button>
            </a-space>
          </a-card>
        </a-col>

        <!-- 오른쪽 패널: 캔버스 -->
        <a-col :span="18" class="canvas-container">
          <a-tabs v-model:activeKey="activePageId" type="card" class="page-tabs">
            <a-tab-pane key="certificateCustomerPage">
              <template #tab>
                <span><file-text-outlined /> 환자 인증서 페이지</span>
              </template>
              <VisualCanvas
                :page="getPageById('certificateCustomerPage')"
                :selected-item="selectedItem"
                @select="selectItem"
                @update="updateItem"
                @remove="removeItem"
                @add-section="handleAddSectionFromCanvas"
              />
            </a-tab-pane>

            <a-tab-pane key="friendPage">
              <template #tab>
                <span><user-outlined /> 친구 페이지</span>
              </template>
              <VisualCanvas
                :page="getPageById('friendPage')"
                :selected-item="selectedItem"
                @select="selectItem"
                @update="updateItem"
                @remove="removeItem"
                @add-section="handleAddSectionFromCanvas"
              />
            </a-tab-pane>

            <a-tab-pane key="hospitalDetailPage">
              <template #tab>
                <span><file-text-outlined /> 병원 상세 페이지</span>
              </template>
              <VisualCanvas
                :page="getPageById('hospitalDetailPage')"
                :selected-item="selectedItem"
                @select="selectItem"
                @update="updateItem"
                @remove="removeItem"
                @add-section="handleAddSectionFromCanvas"
              />
            </a-tab-pane>

            <a-tab-pane key="reviewModal">
              <template #tab>
                <span><file-text-outlined /> 후기 모달</span>
              </template>
              <VisualCanvas
                :page="getPageById('reviewModal')"
                :selected-item="selectedItem"
                @select="selectItem"
                @update="updateItem"
                @remove="removeItem"
                @add-section="handleAddSectionFromCanvas"
              />
            </a-tab-pane>
          </a-tabs>
        </a-col>
      </a-row>
    </div>

    <!-- 속성 편집 드로워 -->
    <a-drawer
      v-model:visible="propertyDrawerVisible"
      placement="right"
      :width="400"
      :body-style="propertyDrawerBodyStyle"
      :class="['property-drawer', propertyDrawerDarkMode ? 'dark-mode' : 'light-mode']"
      :header-style="propertyDrawerHeaderStyle"
    >
      <template #title>
        <div class="drawer-title-wrapper">
          <span>{{ editingItem?.stepId ? '스텝 속성' : editingItem?.sectionId ? '섹션 속성' : '컴포넌트 속성' }}</span>
          <a-tooltip :title="propertyDrawerDarkMode ? '라이트 모드로 전환' : '다크 모드로 전환'">
            <a-button
              type="text"
              size="small"
              @click="togglePropertyDrawerMode"
              :style="{ color: propertyDrawerDarkMode ? '#fff' : '#333' }"
            >
              <bulb-outlined v-if="propertyDrawerDarkMode" />
              <bulb-filled v-else />
            </a-button>
          </a-tooltip>
        </div>
      </template>
      <PropertyEditor
        v-if="editingItem"
        :item="editingItem"
        :dark-mode="propertyDrawerDarkMode"
        @save="saveProperties"
        @cancel="propertyDrawerVisible = false"
      />
    </a-drawer>

    <!-- 미리보기 드로워 -->
    <a-drawer
      v-model:visible="previewDrawerVisible"
      title="페이지 미리보기"
      placement="right"
      :width="previewDrawerWidth"
      :body-style="{ padding: 0 }"
    >
      <div style="height: 100%; display: flex; flex-direction: column;">
        <!-- URL 입력 및 디바이스 선택 영역 -->
        <div style="background: white; border-bottom: 1px solid #f0f0f0;">
          <!-- URL 입력 행 -->
          <div style="padding: 12px 16px 0;">
            <a-input
              v-model:value="previewUrlInput"
              placeholder="미리보기 URL을 입력하세요 (예: https://example.com)"
              @pressEnter="loadPreview"
            >
              <template #prefix>
                <link-outlined style="color: #999;" />
              </template>
            </a-input>
          </div>

          <!-- 버튼 행 -->
          <div style="padding: 8px 16px; display: flex; gap: 8px;">
            <a-button type="primary" @click="loadPreview" class="yellow-button" style="flex: 1;">
              <reload-outlined /> 불러오기
            </a-button>
            <a-button
              v-if="previewUrl"
              @click="refreshPreview"
              style="flex: 1;"
            >
              <reload-outlined /> 새로고침
            </a-button>
          </div>

          <!-- 디바이스 선택 영역 -->
          <div style="padding: 0 16px 12px;">
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <span style="color: #666; font-size: 13px;">화면 크기:</span>
              <a-radio-group v-model:value="previewDeviceCategory" @change="changePreviewCategory" size="small">
                <a-radio-button value="mobile">
                  <mobile-outlined /> 모바일
                </a-radio-button>
                <a-radio-button value="tablet">
                  <tablet-outlined /> 태블릿
                </a-radio-button>
                <a-radio-button value="desktop">
                  <desktop-outlined /> 데스크탑
                </a-radio-button>
              </a-radio-group>
            </div>

            <!-- 모바일 기기 선택 -->
            <div v-if="previewDeviceCategory === 'mobile'" style="display: flex; align-items: center; gap: 12px;">
              <span style="color: #666; font-size: 12px;">기기:</span>
              <a-select
                v-model:value="selectedMobileDevice"
                style="flex: 1;"
                size="small"
                @change="changePreviewDevice"
              >
                <a-select-opt-group label="iPhone">
                  <a-select-option value="iphone-15-pro-max">iPhone 15 Pro Max (430×932)</a-select-option>
                  <a-select-option value="iphone-15-pro">iPhone 15 Pro (393×852)</a-select-option>
                  <a-select-option value="iphone-14">iPhone 14 (390×844)</a-select-option>
                  <a-select-option value="iphone-13-mini">iPhone 13 mini (375×812)</a-select-option>
                  <a-select-option value="iphone-se">iPhone SE (375×667)</a-select-option>
                </a-select-opt-group>
                <a-select-opt-group label="Samsung">
                  <a-select-option value="galaxy-s24-ultra">Galaxy S24 Ultra (412×915)</a-select-option>
                  <a-select-option value="galaxy-s23">Galaxy S23 (360×780)</a-select-option>
                  <a-select-option value="galaxy-z-fold">Galaxy Z Fold5 (412×892)</a-select-option>
                  <a-select-option value="galaxy-z-flip">Galaxy Z Flip5 (360×748)</a-select-option>
                  <a-select-option value="galaxy-a54">Galaxy A54 (384×854)</a-select-option>
                </a-select-opt-group>
                <a-select-opt-group label="기타">
                  <a-select-option value="pixel-8-pro">Pixel 8 Pro (412×915)</a-select-option>
                  <a-select-option value="pixel-7">Pixel 7 (412×892)</a-select-option>
                  <a-select-option value="custom-mobile">커스텀 (360×800)</a-select-option>
                </a-select-opt-group>
              </a-select>
              <a-button
                size="small"
                @click="rotateDevice"
                :disabled="previewDeviceCategory === 'desktop'"
              >
                <swap-outlined /> 회전
              </a-button>
            </div>

            <!-- 태블릿 기기 선택 -->
            <div v-if="previewDeviceCategory === 'tablet'" style="display: flex; align-items: center; gap: 12px;">
              <span style="color: #666; font-size: 12px;">기기:</span>
              <a-select
                v-model:value="selectedTabletDevice"
                style="flex: 1;"
                size="small"
                @change="changePreviewDevice"
              >
                <a-select-option value="ipad-pro-12">iPad Pro 12.9" (1024×1366)</a-select-option>
                <a-select-option value="ipad-pro-11">iPad Pro 11" (834×1194)</a-select-option>
                <a-select-option value="ipad-air">iPad Air (820×1180)</a-select-option>
                <a-select-option value="ipad-mini">iPad Mini (768×1024)</a-select-option>
                <a-select-option value="galaxy-tab-s9">Galaxy Tab S9 (810×1080)</a-select-option>
                <a-select-option value="surface-pro">Surface Pro (912×1368)</a-select-option>
              </a-select>
              <a-button
                size="small"
                @click="rotateDevice"
                :disabled="previewDeviceCategory === 'desktop'"
              >
                <swap-outlined /> 회전
              </a-button>
            </div>

            <!-- 현재 뷰포트 크기 표시 -->
            <div v-if="previewDeviceCategory !== 'desktop'" style="margin-top: 8px; display: flex; justify-content: space-between; align-items: center;">
              <span style="color: #999; font-size: 11px;">
                뷰포트: {{ getCurrentViewportSize() }}
              </span>
              <span v-if="isRotated" style="color: #1890ff; font-size: 11px;">
                <rotate-right-outlined /> 가로 모드
              </span>
            </div>
          </div>
        </div>

        <!-- iframe 컨테이너 -->
        <div style="flex: 1; position: relative; overflow: auto; background: #f0f2f5; display: flex; align-items: flex-start; justify-content: center; padding: 20px;">
          <div
            v-if="previewUrl"
            :style="getPreviewContainerStyle()"
            style="background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.1); transition: all 0.3s ease;"
          >
            <iframe
              ref="previewIframe"
              :src="previewUrl"
              style="width: 100%; height: 100%; border: none;"
              title="페이지 미리보기"
              @load="onIframeLoad"
              @error="onIframeError"
            />
          </div>
          <div v-else style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
            <a-empty description="URL을 입력하여 페이지를 미리보기하세요">
              <template #image>
                <eye-outlined style="font-size: 48px; color: #ccc;" />
              </template>
            </a-empty>
          </div>
        </div>
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { ref, reactive, computed, h, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  AppstoreOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ClearOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  BlockOutlined,
  FontSizeOutlined,
  PictureOutlined,
  LinkOutlined,
  FormOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
  ContainerOutlined,
  BgColorsOutlined,
  ReloadOutlined,
  MobileOutlined,
  TabletOutlined,
  DesktopOutlined,
  SwapOutlined,
  RotateRightOutlined,
  BulbOutlined,
  BulbFilled
} from '@ant-design/icons-vue';
import { message, Modal } from 'ant-design-vue';
import VisualCanvas from './components/VisualCanvas.vue';
import PropertyEditor from './components/PropertyEditor.vue';

export default {
  name: 'PageDesignerSectionV3',
  components: {
    AppstoreOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    ClearOutlined,
    InfoCircleOutlined,
    FileTextOutlined,
    UserOutlined,
    BlockOutlined,
    FontSizeOutlined,
    PictureOutlined,
    LinkOutlined,
    FormOutlined,
    ProfileOutlined,
    QuestionCircleOutlined,
    ThunderboltOutlined,
    ContainerOutlined,
    BgColorsOutlined,
    ReloadOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined,
    SwapOutlined,
    RotateRightOutlined,
    BulbOutlined,
    BulbFilled,
    VisualCanvas,
    PropertyEditor
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();

    // 활성 페이지 - 쿼리 파라미터에서 가져오거나 기본값 사용
    const activePageId = ref(route.query.pageTab || 'certificateCustomerPage');

    // 선택된 아이템
    const selectedItem = ref(null);
    const selectedStep = ref(null);
    const selectedSection = ref(null);

    // 속성 편집기
    const propertyDrawerVisible = ref(false);
    const editingItem = ref(null);
    const propertyDrawerDarkMode = ref(false); // 다크모드 상태

    // 드로워가 닫힐 때 body 클래스 정리
    watch(propertyDrawerVisible, (visible) => {
      if (!visible) {
        document.body.classList.remove('property-drawer-dark-mode');
      }
    });

    // 속성 드로워 스타일 (다크/라이트 모드)
    const propertyDrawerBodyStyle = computed(() => {
      return propertyDrawerDarkMode.value
        ? { background: '#4a4a4a' }
        : { background: '#ffffff' };
    });

    const propertyDrawerHeaderStyle = computed(() => {
      return propertyDrawerDarkMode.value
        ? { background: '#3a3a3a', borderBottom: '1px solid #555' }
        : { background: '#ffffff', borderBottom: '1px solid #f0f0f0' };
    });

    // 다크/라이트 모드 토글
    const togglePropertyDrawerMode = () => {
      propertyDrawerDarkMode.value = !propertyDrawerDarkMode.value;
      // body에 클래스 추가/제거 (Select 드롭다운 스타일링용)
      if (propertyDrawerDarkMode.value) {
        document.body.classList.add('property-drawer-dark-mode');
      } else {
        document.body.classList.remove('property-drawer-dark-mode');
      }
      message.success(propertyDrawerDarkMode.value ? '다크 모드로 전환됨' : '라이트 모드로 전환됨');
    };

    // 미리보기 드로워
    const previewDrawerVisible = ref(false);
    const previewUrl = ref('');
    const previewUrlInput = ref('');
    const previewDeviceCategory = ref('desktop');
    const selectedMobileDevice = ref('iphone-15-pro');
    const selectedTabletDevice = ref('ipad-pro-11');
    const isRotated = ref(false);
    const previewIframe = ref(null);

    // 디바이스 스펙 정의
    const deviceSpecs = {
      // 모바일 디바이스
      'iphone-15-pro-max': { width: 430, height: 932, name: 'iPhone 15 Pro Max' },
      'iphone-15-pro': { width: 393, height: 852, name: 'iPhone 15 Pro' },
      'iphone-14': { width: 390, height: 844, name: 'iPhone 14' },
      'iphone-13-mini': { width: 375, height: 812, name: 'iPhone 13 mini' },
      'iphone-se': { width: 375, height: 667, name: 'iPhone SE' },
      'galaxy-s24-ultra': { width: 412, height: 915, name: 'Galaxy S24 Ultra' },
      'galaxy-s23': { width: 360, height: 780, name: 'Galaxy S23' },
      'galaxy-z-fold': { width: 412, height: 892, name: 'Galaxy Z Fold5' },
      'galaxy-z-flip': { width: 360, height: 748, name: 'Galaxy Z Flip5' },
      'galaxy-a54': { width: 384, height: 854, name: 'Galaxy A54' },
      'pixel-8-pro': { width: 412, height: 915, name: 'Pixel 8 Pro' },
      'pixel-7': { width: 412, height: 892, name: 'Pixel 7' },
      'custom-mobile': { width: 360, height: 800, name: 'Custom Mobile' },
      // 태블릿 디바이스
      'ipad-pro-12': { width: 1024, height: 1366, name: 'iPad Pro 12.9"' },
      'ipad-pro-11': { width: 834, height: 1194, name: 'iPad Pro 11"' },
      'ipad-air': { width: 820, height: 1180, name: 'iPad Air' },
      'ipad-mini': { width: 768, height: 1024, name: 'iPad Mini' },
      'galaxy-tab-s9': { width: 810, height: 1080, name: 'Galaxy Tab S9' },
      'surface-pro': { width: 912, height: 1368, name: 'Surface Pro' },
    };

    // 현재 선택된 디바이스 정보 가져오기
    const getCurrentDevice = () => {
      if (previewDeviceCategory.value === 'mobile') {
        return deviceSpecs[selectedMobileDevice.value];
      } else if (previewDeviceCategory.value === 'tablet') {
        return deviceSpecs[selectedTabletDevice.value];
      }
      return null;
    };

    // 미리보기 드로워 너비 계산
    const previewDrawerWidth = computed(() => {
      if (previewDeviceCategory.value === 'desktop') {
        return 1200;
      }
      const device = getCurrentDevice();
      if (device) {
        const width = isRotated.value ? device.height : device.width;
        // 패딩과 여백을 위해 추가 공간 확보
        return Math.min(width + 100, 1400);
      }
      return 600;
    });

    // 기본 컴포넌트 정의 (API 스펙에 맞춤)
    const basicComponents = [
      { type: 'text', label: '텍스트', icon: 'font-size-outlined', description: '텍스트 요소' },
      { type: 'button', label: '버튼', icon: 'thunderbolt-outlined', description: '클릭 버튼' },
      { type: 'image', label: '이미지', icon: 'picture-outlined', description: '이미지 요소' },
      { type: 'backgroundImage', label: '배경 이미지', icon: 'bg-colors-outlined', description: '배경 이미지' },
      { type: 'survey', label: '설문문항', icon: 'form-outlined', description: '설문 조사 문항' },
      { type: 'tooltip', label: '툴팁', icon: 'question-circle-outlined', description: '도움말 툴팁' }
    ];

    // 섹션 타입 정의 (속성으로만 사용)
    const sectionTypes = ['header', 'hero', 'content', 'quiz', 'form', 'custom'];

    // 페이지 가져오기
    const getPageById = (pageId) => {
      return props.form.eventPages.find(p => p.pageId === pageId) || null;
    };

    // 페이지 탭 변경 시 URL 업데이트
    watch(activePageId, (newPageId) => {
      const query = { ...route.query };
      if (newPageId && newPageId !== 'certificateCustomerPage') {
        query.pageTab = newPageId;
      } else {
        delete query.pageTab;
      }
      router.replace({ query });
    });

    // 라우트 쿼리 변경 감지
    watch(() => route.query.pageTab, (newPageTab) => {
      if (newPageTab && newPageTab !== activePageId.value) {
        activePageId.value = newPageTab;
      } else if (!newPageTab && activePageId.value !== 'certificateCustomerPage') {
        activePageId.value = 'certificateCustomerPage';
      }
    });


    // 컴포넌트 추가 핸들러
    const handleAddComponent = (compType) => {
      // 선택된 섹션이 있는지 확인
      if (!selectedSection.value) {
        // 섹션이 없으면 안내 메시지
        const page = getPageById(activePageId.value);
        if (!page || !page.steps || page.steps.length === 0) {
          message.warning('먼저 스텝과 섹션을 추가해주세요');
          return;
        }

        const targetStep = page.steps[0];
        if (!targetStep.sections || targetStep.sections.length === 0) {
          message.warning('먼저 섹션을 추가해주세요');
          return;
        }

        selectedSection.value = targetStep.sections[0];
      }

      const section = selectedSection.value;

      const newComponent = {
        id: `${compType.type}-${Date.now()}`,
        type: compType.type,
        content: compType.type === 'text'
          ? { txt: '텍스트 내용을 입력하세요' }
          : compType.type === 'button'
          ? { txt: '버튼 텍스트' }
          : compType.type === 'image'
          ? { imageUrl: '' }
          : compType.type === 'tooltip'
          ? { text: '툴팁 내용을 입력하세요', position: 'top' }
          : {},
        order: section.components ? section.components.length + 1 : 1,
        isActive: true
      };

      if (!section.components) section.components = [];
      section.components.push(newComponent);

      message.success(`${compType.label} 컴포넌트가 추가되었습니다`);
    };

    // 아이템 선택
    const selectItem = (item) => {
      selectedItem.value = item;

      // 스텝인지 섹션인지 판단
      if (item.stepId) {
        selectedStep.value = item;
        selectedSection.value = null;
      } else if (item.sectionId) {
        selectedSection.value = item;
        // 부모 스텝 찾기
        const page = getPageById(activePageId.value);
        if (page && page.steps) {
          for (const step of page.steps) {
            if (step.sections && step.sections.some(s => s.sectionId === item.sectionId)) {
              selectedStep.value = step;
              break;
            }
          }
        }
      }
    };

    // 속성 저장
    const saveProperties = (updatedItem) => {
      // 스텝 이름이 비어있으면 기본값 설정
      if (updatedItem.stepId && (!updatedItem.stepName || !updatedItem.stepName.trim())) {
        updatedItem.stepName = 'Unnamed Step';
      }
      // 섹션 이름이 비어있으면 기본값 설정
      if (updatedItem.sectionId && (!updatedItem.sectionName || !updatedItem.sectionName.trim())) {
        updatedItem.sectionName = 'Unnamed Section';
      }

      Object.assign(editingItem.value, updatedItem);
      propertyDrawerVisible.value = false;
      message.success('속성이 저장되었습니다');
    };

    // 아이템 업데이트
    const updateItem = (item, updates) => {
      if (updates.editing) {
        editingItem.value = item;
        propertyDrawerVisible.value = true;
      } else {
        Object.assign(item, updates);
      }
    };

    // 아이템 삭제
    const removeItem = (item, parent) => {
      if (parent.steps && item.stepId) {
        const index = parent.steps.findIndex(s => s.stepId === item.stepId);
        if (index > -1) parent.steps.splice(index, 1);
      } else if (parent.sections && item.sectionId) {
        const index = parent.sections.findIndex(s => s.sectionId === item.sectionId);
        if (index > -1) parent.sections.splice(index, 1);
      } else if (parent.components && item.id) {
        const index = parent.components.findIndex(c => c.id === item.id);
        if (index > -1) parent.components.splice(index, 1);
      }
      message.success('삭제되었습니다');
    };

    // 새 스텝 추가
    const addNewStep = () => {
      const page = getPageById(activePageId.value);
      if (!page) return;

      if (!page.steps) page.steps = [];

      const newStep = {
        stepId: `step-${Date.now()}`,
        stepName: `Step ${page.steps.length + 1}`,
        stepType: 'main',
        order: page.steps.length + 1,
        isActive: true,
        sections: []
      };

      page.steps.push(newStep);
      selectedStep.value = newStep;
      message.success('새 스텝이 추가되었습니다');
    };

    // 모두 지우기
    const clearAll = () => {
      Modal.confirm({
        title: '페이지 초기화',
        content: '현재 페이지의 모든 내용을 삭제하시겠습니까?',
        okText: '삭제',
        cancelText: '취소',
        okButtonProps: {
          danger: true
        },
        onOk: () => {
          const page = getPageById(activePageId.value);
          if (!page) return;

          page.steps = [];
          selectedItem.value = null;
          selectedStep.value = null;
          selectedSection.value = null;
          message.success('페이지가 초기화되었습니다');
        }
      });
    };

    // 미리보기
    const previewPage = () => {
      previewDrawerVisible.value = true;
    };

    // 미리보기 URL 불러오기
    const loadPreview = () => {
      if (!previewUrlInput.value) {
        message.warning('URL을 입력해주세요');
        return;
      }

      // URL 유효성 검사
      try {
        const url = new URL(previewUrlInput.value);
        previewUrl.value = previewUrlInput.value;
        message.success('페이지를 불러오는 중입니다...');
      } catch (error) {
        message.error('유효한 URL을 입력해주세요 (예: https://example.com)');
      }
    };

    // 미리보기 새로고침
    const refreshPreview = () => {
      if (previewIframe.value && previewUrl.value) {
        // iframe을 강제로 새로고침
        // 방법 1: src를 다시 설정
        const currentSrc = previewUrl.value;
        previewUrl.value = '';
        setTimeout(() => {
          previewUrl.value = currentSrc;
          message.success('페이지를 새로고침하는 중입니다...');
        }, 0);
      }
    };

    // iframe 로드 성공
    const onIframeLoad = () => {
      // iframe이 성공적으로 로드되었을 때
      console.log('iframe loaded successfully');
    };

    // iframe 로드 실패
    const onIframeError = (event) => {
      console.error('iframe load error:', event);
      message.error('페이지를 불러오는데 실패했습니다. CORS 정책으로 인해 일부 사이트는 미리보기가 제한될 수 있습니다.');
    };

    // 캔버스에서 섹션 추가 요청 처리
    const handleAddSectionFromCanvas = (step) => {
      // 기본 섹션 추가 (타입 없음)
      const newSection = {
        sectionId: `section-${Date.now()}`,
        sectionName: '새 섹션',  // sectionName 필드 추가 (API에 추가됨)
        sectionType: 'content',  // 기본 타입을 일반 콘텐츠로
        order: step.sections ? step.sections.length + 1 : 1,
        components: [],
        metadata: {}
      };

      if (!step.sections) {
        step.sections = [];
      }
      step.sections.push(newSection);

      // 추가된 섹션 선택
      selectedSection.value = newSection;
      selectedStep.value = step;

      message.success('섹션이 추가되었습니다. 속성 패널에서 타입을 변경할 수 있습니다.');
    };

    // 현재 뷰포트 크기 정보 반환
    const getCurrentViewportSize = () => {
      const device = getCurrentDevice();
      if (device) {
        const width = isRotated.value ? device.height : device.width;
        const height = isRotated.value ? device.width : device.height;
        return `${width} × ${height}px`;
      }
      return '';
    };

    // 미리보기 컨테이너 스타일 반환
    const getPreviewContainerStyle = () => {
      const styles = {
        background: 'white',
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden'
      };

      if (previewDeviceCategory.value === 'desktop') {
        return {
          ...styles,
          width: '100%',
          height: '100%',
          maxWidth: '1400px'
        };
      }

      const device = getCurrentDevice();
      if (device) {
        const width = isRotated.value ? device.height : device.width;
        const height = isRotated.value ? device.width : device.height;

        // 모바일과 태블릿에 대한 스타일링
        const isMobile = previewDeviceCategory.value === 'mobile';

        return {
          ...styles,
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: isMobile ? '30px' : '20px',
          border: isMobile ? '12px solid #222' : '16px solid #444',
          // 노치/홈 인디케이터 시뮬레이션 (iPhone)
          ...(isMobile && selectedMobileDevice.value.includes('iphone') && !selectedMobileDevice.value.includes('se') ? {
            paddingTop: '44px', // 상태바 영역
            paddingBottom: '34px' // 홈 인디케이터 영역
          } : {})
        };
      }

      return styles;
    };

    // 디바이스 카테고리 변경 핸들러
    const changePreviewCategory = () => {
      isRotated.value = false; // 카테고리 변경 시 회전 초기화
      const categoryNames = {
        'mobile': '모바일',
        'tablet': '태블릿',
        'desktop': '데스크탑'
      };
      message.success(`${categoryNames[previewDeviceCategory.value]} 카테고리로 변경되었습니다`);
    };

    // 디바이스 변경 핸들러
    const changePreviewDevice = () => {
      const device = getCurrentDevice();
      if (device) {
        message.success(`${device.name}로 변경되었습니다`);
      }
    };

    // 디바이스 회전 핸들러
    const rotateDevice = () => {
      if (previewDeviceCategory.value !== 'desktop') {
        isRotated.value = !isRotated.value;
        const orientation = isRotated.value ? '가로' : '세로';
        message.success(`${orientation} 모드로 전환되었습니다`);
      }
    };

    return {
      activePageId,
      selectedItem,
      basicComponents,
      propertyDrawerVisible,
      editingItem,
      propertyDrawerDarkMode,
      propertyDrawerBodyStyle,
      propertyDrawerHeaderStyle,
      previewDrawerVisible,
      previewDrawerWidth,
      previewUrl,
      previewUrlInput,
      previewDeviceCategory,
      selectedMobileDevice,
      selectedTabletDevice,
      isRotated,
      previewIframe,
      // Methods
      getPageById,
      handleAddComponent,
      handleAddSectionFromCanvas,
      selectItem,
      saveProperties,
      updateItem,
      removeItem,
      addNewStep,
      clearAll,
      previewPage,
      loadPreview,
      refreshPreview,
      onIframeLoad,
      onIframeError,
      getCurrentViewportSize,
      getPreviewContainerStyle,
      changePreviewCategory,
      changePreviewDevice,
      rotateDevice,
      togglePropertyDrawerMode,
      h
    };
  }
};
</script>

<style lang="scss" scoped>
.page-designer-v3 {
  .designer-header {
    margin-bottom: 24px;

    .section-title {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .section-description {
      color: #666;
      margin-bottom: 16px;
    }

    .hierarchy-guide {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      background: #f0f5ff;
      border-radius: 8px;
      border: 1px solid #d6e4ff;
      margin-bottom: 16px;

      .hierarchy-item {
        display: flex;
        align-items: center;
        margin-right: 12px;

        .hierarchy-label {
          padding: 4px 12px;
          background: white;
          border: 1px solid #d9d9d9;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #595959;
        }

        .hierarchy-arrow {
          margin: 0 12px;
          color: #bfbfbf;
          font-size: 16px;
        }

        &:last-child {
          margin-right: 0;

          .hierarchy-label {
            background: #1890ff;
            border-color: #1890ff;
            color: white;
          }
        }
      }
    }
  }

  // Visual Builder
  .visual-builder {
    .builder-container {
      min-height: 600px;
    }

    .component-palette {
      .palette-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        margin-top: 8px;
      }

      .palette-item {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 12px 8px;
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.3s ease;
        background: white;

        &:hover {
          border-color: #1890ff;
          background: rgba(24, 144, 255, 0.05);
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        &.component {
          grid-column: span 1;
        }

        .palette-icon {
          font-size: 20px;
          margin-bottom: 4px;
          color: #1890ff;
        }

        span {
          font-size: 12px;
          text-align: center;
        }

        .info-icon {
          position: absolute;
          top: 4px;
          right: 4px;
          font-size: 12px;
          color: #999;
        }
      }

      .palette-help {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
        padding: 8px;
        background: #f0f5ff;
        border-radius: 4px;
        font-size: 12px;
        color: #666;
      }
    }

    .canvas-container {
      .page-tabs {
        :deep(.ant-tabs-content) {
          background: #f5f5f5;
          border-radius: 8px;
          padding: 16px;
          min-height: 500px;
        }
      }
    }
  }

  // 노란색 버튼
  .yellow-button {
    background-color: #FFCC32;
    border-color: #FFCC32;

    &:hover {
      background-color: #FFB800;
      border-color: #FFB800;
    }
  }
}

// Popconfirm 스타일 오버라이드 (전역 적용)
:deep(.ant-popover-inner-content) {
  .ant-popconfirm-buttons {
    display: flex;
    flex-direction: row !important;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;

    .ant-btn {
      margin: 0 !important;
    }
  }
}

// 드로워 타이틀 래퍼
.drawer-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 24px;
}

// 속성 편집 드로워 - 다크 모드
:deep(.property-drawer.dark-mode) {
  // 드로워 헤더 스타일
  .ant-drawer-header {
    .ant-drawer-title {
      color: #fff;
    }

    .ant-drawer-close {
      color: #fff;

      &:hover {
        color: #ffcc32;
      }
    }
  }

  // 폼 라벨 스타일
  .ant-form-item-label > label {
    color: #fff;
  }

  // 폼 아이템 설명 텍스트
  .ant-form-item-explain {
    color: #ccc;
  }

  // 라디오, 체크박스 텍스트
  .ant-radio-wrapper,
  .ant-checkbox-wrapper {
    color: #fff;
  }

  // Select 옵션 그룹 라벨
  .ant-select-selection-item {
    color: #fff !important;
  }

  // Select 셀렉터 배경색
  .ant-select-selector {
    background-color: #3a3a3a !important;
    border-color: #555 !important;
  }

  // Select 화살표
  .ant-select-arrow {
    color: #aaa !important;
  }

  // Input 필드
  .ant-input,
  .ant-input-number,
  .ant-textarea {
    background-color: #3a3a3a !important;
    color: #fff !important;
    border-color: #555 !important;

    &::placeholder {
      color: #aaa !important;
    }
  }

  // 비활성화된 입력 필드
  .ant-input[disabled],
  .ant-input-disabled {
    background-color: #2a2a2a !important;
    color: #888 !important;
  }

  // 라디오 버튼 그룹
  .ant-radio-button-wrapper {
    background-color: #3a3a3a !important;
    color: #fff !important;
    border-color: #555 !important;

    &:hover {
      color: #40a9ff !important;
    }

    &.ant-radio-button-wrapper-checked {
      background-color: #1890ff !important;
      border-color: #1890ff !important;
    }
  }

  // 버튼 스타일
  .ant-btn-default {
    background-color: #3a3a3a !important;
    color: #fff !important;
    border-color: #555 !important;

    &:hover {
      border-color: #40a9ff !important;
      color: #40a9ff !important;
    }
  }

  .ant-btn-text {
    color: #fff !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
    }
  }

  // 스위치 라벨
  .ant-switch {
    background: #666;

    &.ant-switch-checked {
      background: #ffcc32;
    }
  }

  // 태그 스타일 조정
  .ant-tag {
    border: 1px solid #666;
  }

  // PropertyEditor footer 배경
  .property-editor-footer {
    background: #3a3a3a !important;
    border-top: 1px solid #555 !important;
  }
}

// 속성 편집 드로워 - 라이트 모드
:deep(.property-drawer.light-mode) {
  // 드로워 헤더 스타일
  .ant-drawer-header {
    .ant-drawer-title {
      color: #333;
    }

    .ant-drawer-close {
      color: #333;

      &:hover {
        color: #ffcc32;
      }
    }
  }

  // 폼 라벨 스타일
  .ant-form-item-label > label {
    color: #333;
  }

  // 폼 아이템 설명 텍스트
  .ant-form-item-explain {
    color: #666;
  }

  // 라디오, 체크박스 텍스트
  .ant-radio-wrapper,
  .ant-checkbox-wrapper {
    color: #333;
  }

  // Select 옵션 그룹 라벨
  .ant-select-selection-item {
    color: #333;
  }

  // 스위치 라벨
  .ant-switch {
    background: #ccc;

    &.ant-switch-checked {
      background: #ffcc32;
    }
  }

  // 태그 스타일 조정
  .ant-tag {
    border: 1px solid #d9d9d9;
  }

  // PropertyEditor footer 배경
  .property-editor-footer {
    background: #fff !important;
    border-top: 1px solid #f0f0f0 !important;
  }
}
</style>

<style lang="scss">
// 글로벌 스타일 - Select 드롭다운 다크모드 (포털로 렌더링되어 scoped 밖에 있음)
// 다크모드 드로워가 열려있을 때 Select 드롭다운 스타일
.property-drawer.dark-mode + .ant-select-dropdown,
body.property-drawer-dark-mode .ant-select-dropdown {
  background-color: #3a3a3a !important;

  .ant-select-item {
    color: #fff !important;

    &:hover {
      background-color: #4a4a4a !important;
    }

    &.ant-select-item-option-selected {
      background-color: #1890ff !important;
    }

    &.ant-select-item-option-active {
      background-color: #4a4a4a !important;
    }
  }

  .ant-select-item-option-content {
    color: #fff !important;
  }

  // Badge 색상 아이콘 표시
  .ant-select-item .badge-color {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 8px;
  }
}
</style>